import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Breadcrumb,
  LoadingEvent,
  SnackbarApiErrorMessage,
  useDataProvider,
  useLocalization,
  useStore,
} from '@ui-framework/index';
import { IconLibrary, PageSection } from '@product-framework/index';
import Layout from '@product-framework/components/Layout';
import Viewers, { FILE_TYPE } from '@product-framework/components/Viewers';
import { useAppVersion } from '@product-framework/router/AppVersionContext';
import PublicHelpLayout from '../public/layout';
import getHelpDocuments from '../../api';

function HelpDetail({ publicMode = false }) {
  const { id: slug } = useParams();
  const navigate = useNavigate();
  const { formatMessage } = useLocalization();
  const store = useStore();
  const locale = store.domain.locale.get().toLowerCase();
  const provider = useDataProvider('issuesTracking');
  const { appVersion, loading: versionLoading } = useAppVersion();
  const [selection, setSelection] = useState(null);
  const language =
    selection?.slug === slug && selection?.locale === locale
      ? selection.language
      : locale;
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [showFile, setShowFile] = useState(FILE_TYPE.PDF);
  const document = result?.ok ? result.data : null;
  // Fall back to whichever file the variant actually has.
  const hasChosenFile =
    showFile === FILE_TYPE.VIDEO ? document?.videoUrl : document?.pdfUrl;
  const activeFile = hasChosenFile
    ? showFile
    : (showFile === FILE_TYPE.VIDEO && FILE_TYPE.PDF) || FILE_TYPE.VIDEO;
  const breadcrumbs = [
    {
      name: formatMessage({
        id: 'HELP_TRAINING',
        defaultMessage: 'Help & Training',
      }),
      icon: IconLibrary.Help,
      onClick: () => navigate(publicMode ? '/public/help' : '/help'),
    },
    ...(document ? [{ name: document.title, icon: IconLibrary.Help }] : []),
  ];
  const PageLayout = publicMode ? PublicHelpLayout : Layout;

  useEffect(() => {
    if (versionLoading || !appVersion) return undefined;
    const controller = new AbortController();
    setLoading(true);
    getHelpDocuments(
      provider,
      { appVersion, language },
      { publicMode, slug, signal: controller.signal },
    ).then(response => {
      if (!controller.signal.aborted) {
        setResult(response);
        setShowFile(response.data?.videoUrl ? FILE_TYPE.VIDEO : FILE_TYPE.PDF);
        setLoading(false);
      }
    });
    return () => controller.abort();
  }, [provider, appVersion, versionLoading, language, slug, publicMode]);

  if (!versionLoading && !appVersion) {
    return (
      <PageLayout {...(publicMode ? { breadcrumbs } : {})}>
        <Alert severity="error">
          {formatMessage({
            id: 'HELP_VERSION_UNAVAILABLE',
            defaultMessage:
              'Unable to determine the app version. Reload the page to try again.',
          })}
        </Alert>
      </PageLayout>
    );
  }

  return (
    <PageLayout {...(publicMode ? { breadcrumbs } : {})}>
      {!publicMode && <Breadcrumb items={breadcrumbs} />}
      {(loading || versionLoading) && (
        <LoadingEvent>
          {formatMessage({
            id: 'FETCHING_HELP_DOCUMENTS',
            defaultMessage: 'Fetching help documents...',
          })}
        </LoadingEvent>
      )}
      {!loading && !versionLoading && !document && (
        <Alert severity="error">
          <SnackbarApiErrorMessage errors={result?.errors} />
        </Alert>
      )}
      {!loading && !versionLoading && document && (
        <PageSection label={document.title}>
          <Stack spacing={2}>
            <TextField
              select
              size="small"
              sx={{ maxWidth: 240 }}
              label={formatMessage({
                id: 'LANGUAGE',
                defaultMessage: 'Language',
              })}
              value={document.languageCode}
              onChange={event =>
                setSelection({ slug, locale, language: event.target.value })
              }>
              {(document.availableLanguages || []).map(code => (
                <MenuItem key={code} value={code}>
                  {code.toUpperCase()}
                </MenuItem>
              ))}
            </TextField>
            {document.pdfUrl && document.videoUrl && (
              <Stack direction="row" spacing={1}>
                <Button
                  onClick={() => setShowFile(FILE_TYPE.PDF)}
                  disabled={showFile === FILE_TYPE.PDF}>
                  {formatMessage({
                    id: 'VIEW_PDF',
                    defaultMessage: 'View PDF',
                  })}
                </Button>
                <Button
                  onClick={() => setShowFile(FILE_TYPE.VIDEO)}
                  disabled={showFile === FILE_TYPE.VIDEO}>
                  {formatMessage({
                    id: 'WATCH_VIDEO',
                    defaultMessage: 'Watch Video',
                  })}
                </Button>
              </Stack>
            )}
            {/* The shared viewers frame media in black; readers get a plain frame. */}
            <Box
              sx={{
                '& > div > div': { border: 'none !important' },
                '& media-controller': {
                  '--media-background-color': 'transparent',
                  height: 'auto !important',
                },
              }}>
              {(document.pdfUrl || document.videoUrl) && (
                <Viewers
                  key={`${document.id}-${document.languageCode}-${activeFile}`}
                  file={
                    activeFile === FILE_TYPE.VIDEO
                      ? document.videoUrl
                      : document.pdfUrl
                  }
                  fileType={activeFile}
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              {document.description}
            </Typography>
          </Stack>
        </PageSection>
      )}
    </PageLayout>
  );
}
export default observer(HelpDetail);
